import React, { useLayoutEffect, useState } from 'react';

import { ThemeProvider } from '@change/ds-core-ui';
import { TextField } from '@change/ds-forms';

type Props = {
	currencySymbol: string;
	errorMessage: string | null;
	onChange: (value: string) => void;
	onFocus: () => void;
	value: number;
	placeholder?: string;
	height?: `${number}px`;
};

export const AmountInput: React.FC<Props> = ({
	currencySymbol,
	errorMessage,
	onChange,
	onFocus,
	value,
	placeholder,
	height = '50px',
}) => {
	const currencyRef = React.useRef<HTMLSpanElement>(null);
	const [paddingLeft, setPaddingLeft] = useState<number | null>(null);

	// Dynamically set the padding left based on the currency symbol width
	useLayoutEffect(() => {
		if (currencyRef.current) {
			const symbolWidth = currencyRef.current.offsetWidth;
			// 12px = left-3 && 4px (gap) = space between currency symbol and input
			setPaddingLeft(Math.round(symbolWidth + 12 + 4));
		}
	}, [currencySymbol]);

	return (
		<ThemeProvider>
			<TextField
				inputMode="numeric"
				isReadOnly={false}
				label="amount"
				labelIsVisible={false}
				name="amount"
				onChange={onChange}
				onFocus={onFocus}
				placeholder={placeholder}
				type="text"
				value={value !== 0 ? String(value) : ''}
				inputOptions={{
					className: 'font-light text-typography-strong text-base bg-white',
					style: {
						height,
						...(paddingLeft !== null && { paddingLeft: `${paddingLeft}px` }),
					},
					// TODO: Remove this casting when TextField props are typed to allow these attributes
					//       https://change.atlassian.net/browse/CHANGE-77254
					...({ 'data-qa': 'custom-amount-input', 'data-testid': 'payment-form-amount-input' } as EmptyIntersection),
					leadingIcon: (
						<ThemeProvider>
							<span
								ref={currencyRef}
								className="text-typography-strong pointer-events-none absolute top-1/2 left-3 inline-flex -translate-y-1/2 items-center justify-center text-base font-light"
							>
								{currencySymbol}
							</span>
						</ThemeProvider>
					),
					pattern: '[0-9]*',
				}}
				isInvalid={!!errorMessage}
			/>
		</ThemeProvider>
	);
};
